/**
 * InfinitSales - Widgets do site: modal de editar widget.
 *
 * Tres abas (aparencia, comportamento, seguranca) e UM Salvar: um PUT so,
 * com o widget_settings mesclado por cima do que o widget ja tem (logo,
 * formulario e o resto que a tela nao mostra passam intactos). Gerar novas
 * chaves e desligar/religar agem na hora, pelos mesmos handlers do menu do
 * card (onRotate/onToggle devolvem false quando o confirm e cancelado).
 * Abas no visual do SavedMessagesDrawer (sublinhado no verde da marca).
 */
import React, { useId, useState } from 'react';
import { Loader2, Power, RefreshCw } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import widgetService from '../../services/widget/widgetService';
import WidgetModalShell, { ErrorBox, MONO, PAD_X, errorMessage, formStyles } from './WidgetModalShell';

const TABS = [
  { id: 'look', label: 'Aparência' },
  { id: 'behavior', label: 'Comportamento' },
  { id: 'security', label: 'Segurança' },
];

// '#abc', 'abc' ou '#aabbcc' -> '#aabbcc'; invalido -> null.
const toHex = (value) => {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(String(value || '').trim());
  if (!m) return null;
  const h = m[1].length === 3 ? m[1].split('').map((c) => c + c).join('') : m[1];
  return `#${h.toLowerCase()}`;
};
const toLines = (list) => (Array.isArray(list) ? list.join('\n') : '');
const fromLines = (text) => String(text || '').split(/\n|,/).map((x) => x.trim()).filter(Boolean);

const spin = { animation: 'spin 1s linear infinite' };

const WidgetEditModal = ({ initial, onClose, onSaved, onRotate, onToggle }) => {
  const { colors } = useTheme();
  const s = formStyles(colors);
  const uid = useId();
  const [tab, setTab] = useState('look');
  const [form, setForm] = useState(() => {
    const st = initial?.widget_settings || {};
    return {
      name: initial?.name || '',
      title: st.title || '',
      color: st.primary_color || '',
      welcome_message: st.welcome_message || '',
      mode: initial?.mode === 'standalone' ? 'standalone' : 'embed',
      position: st.position === 'left' ? 'left' : 'right',
      hide_on: toLines(st.hide_on),
      away_message: st.away_message || '',
      show_member_names: st.show_member_names !== false,
      allowed_domains: toLines(initial?.allowed_domains),
    };
  });
  const [saving, setSaving] = useState(false);
  const [acting, setActing] = useState(null); // 'rotate' | 'toggle' | null
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const disconnected = initial?.connection_status === 'disconnected';

  const submit = async (e) => {
    e.preventDefault();
    if (saving || acting) return;
    if (!form.name.trim()) { setTab('look'); setError('Dê um nome ao widget.'); return; }
    const color = toHex(form.color);
    if (form.color.trim() && !color) { setTab('look'); setError('Use uma cor no formato #RRGGBB.'); return; }
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const settings = {
        ...(initial?.widget_settings || {}),
        title: form.title,
        welcome_message: form.welcome_message,
        position: form.position,
        hide_on: fromLines(form.hide_on),
        away_message: form.away_message,
        show_member_names: form.show_member_names,
      };
      if (color) settings.primary_color = color;
      const res = await widgetService.updateAccount(initial.id, {
        name: form.name.trim(),
        mode: form.mode,
        allowed_domains: fromLines(form.allowed_domains),
        widget_settings: settings,
      });
      onSaved?.(res?.data || null);
    } catch (err) {
      setError(errorMessage(err, 'Não foi possível salvar o widget.'));
    } finally {
      setSaving(false);
    }
  };

  // Acoes imediatas da aba Seguranca (nao dependem do Salvar).
  const act = async (kind) => {
    if (saving || acting) return;
    setError(null);
    setNotice(null);
    setActing(kind);
    try {
      const done = kind === 'rotate' ? await onRotate?.(initial) : await onToggle?.(initial);
      if (done && kind === 'rotate') setNotice('Chaves novas geradas.');
    } catch (err) {
      setError(errorMessage(err, 'Não foi possível concluir a ação.'));
    } finally {
      setActing(null);
    }
  };

  const tabs = (
    <div role="tablist" style={{
      display: 'flex', flexShrink: 0, marginTop: 14, padding: `0 ${PAD_X}`,
      borderBottom: `1px solid ${colors.borderColor}`,
    }}>
      {TABS.map((t) => {
        const active = tab === t.id;
        return (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={active}
            className="wdg-btn"
            onClick={() => setTab(t.id)}
            style={{
              flex: 1, minWidth: 0, padding: '10px 6px', marginBottom: -1, cursor: 'pointer',
              backgroundColor: 'transparent',
              borderBottom: `2px solid ${active ? colors.accentPrimary : 'transparent'}`,
              color: active ? colors.accentPrimary : colors.textSecondary,
              fontSize: 13, fontWeight: active ? 600 : 500,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );

  const field = (key, label, input, hint = null, last = false) => (
    <div style={last ? undefined : s.field}>
      <label htmlFor={`${uid}-${key}`} style={s.label}>{label}</label>
      {input}
      {hint && <div style={s.hint}>{hint}</div>}
    </div>
  );
  const text = (key, props = {}) => (
    <input
      id={`${uid}-${key}`}
      className="wdg-field"
      style={s.input}
      value={form[key]}
      onChange={(e) => set(key, e.target.value)}
      {...props}
    />
  );
  const area = (key, props = {}) => (
    <textarea
      id={`${uid}-${key}`}
      className="wdg-field"
      style={s.textarea}
      rows={3}
      value={form[key]}
      onChange={(e) => set(key, e.target.value)}
      {...props}
    />
  );

  return (
    <WidgetModalShell
      title="Editar widget"
      onClose={onClose}
      onSubmit={submit}
      maxWidth={520}
      bodyMinHeight={410}
      tabs={tabs}
      footer={(
        <>
          {error && <ErrorBox>{error}</ErrorBox>}
          <button type="submit" className="wdg-btn" disabled={saving || !!acting} style={s.primaryBtn}>
            {saving && <Loader2 size={16} style={spin} />}
            Salvar
          </button>
        </>
      )}
    >
      {tab === 'look' && (
        <>
          {field('name', 'Nome interno', text('name', { maxLength: 80 }))}
          {field('title', 'Título do chat', text('title'))}
          {field('color', 'Cor', (
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="color"
                aria-label="Escolher cor"
                className="wdg-field"
                value={toHex(form.color) || '#000000'}
                onChange={(e) => set('color', e.target.value)}
                style={{
                  width: 48, flexShrink: 0, padding: 3, cursor: 'pointer', borderRadius: 10,
                  border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgSecondary,
                }}
              />
              {text('color', {
                maxLength: 7, placeholder: '#RRGGBB', spellCheck: false, autoCapitalize: 'none',
                style: { ...s.input, flex: 1, minWidth: 0, fontFamily: MONO },
              })}
            </div>
          ))}
          {field('welcome_message', 'Mensagem de boas-vindas', area('welcome_message'), null, true)}
        </>
      )}

      {tab === 'behavior' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', columnGap: 12 }}>
            {field('mode', 'Onde aparece', (
              <select id={`${uid}-mode`} className="wdg-field" style={s.input} value={form.mode} onChange={(e) => set('mode', e.target.value)}>
                <option value="embed">Botão no site</option>
                <option value="standalone">Página inteira</option>
              </select>
            ))}
            {field('position', 'Posição do botão', (
              <select id={`${uid}-position`} className="wdg-field" style={s.input} value={form.position} onChange={(e) => set('position', e.target.value)}>
                <option value="right">Direita</option>
                <option value="left">Esquerda</option>
              </select>
            ))}
          </div>
          {field('hide_on', 'Não mostrar nestas páginas', area('hide_on', { placeholder: '/checkout', spellCheck: false }), 'Um caminho por linha.')}
          {field('away_message', 'Mensagem de ausência', area('away_message'))}
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 14, color: colors.textPrimary, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={form.show_member_names}
              onChange={(e) => set('show_member_names', e.target.checked)}
              style={{ width: 16, height: 16, marginTop: 2, flexShrink: 0, accentColor: colors.accentPrimary, cursor: 'pointer' }}
            />
            <span>Mostrar o nome do atendente ao visitante</span>
          </label>
        </>
      )}

      {tab === 'security' && (
        <>
          {field(
            'allowed_domains',
            'Domínios autorizados',
            area('allowed_domains', { placeholder: 'www.minhaloja.com.br', spellCheck: false, autoCapitalize: 'none' }),
            'Um por linha. Vazio aceita qualquer site.',
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <button
              type="button"
              className="wdg-btn"
              disabled={saving || !!acting}
              onClick={() => act('rotate')}
              style={{ ...s.secondaryBtn, flex: '1 1 180px' }}
            >
              {acting === 'rotate' ? <Loader2 size={15} style={spin} /> : <RefreshCw size={15} />}
              Gerar novas chaves
            </button>
            <button
              type="button"
              className="wdg-btn"
              disabled={saving || !!acting}
              onClick={() => act('toggle')}
              style={{ ...(disconnected ? s.secondaryBtn : s.dangerBtn), flex: '1 1 180px' }}
            >
              {acting === 'toggle' ? <Loader2 size={15} style={spin} /> : <Power size={15} />}
              {disconnected ? 'Religar' : 'Desligar widget'}
            </button>
          </div>
          {notice && (
            <div role="status" style={{ marginTop: 10, fontSize: 13, color: colors.accentPrimary }}>{notice}</div>
          )}
        </>
      )}
    </WidgetModalShell>
  );
};

export default WidgetEditModal;
